import { useCallback, useLayoutEffect, useRef } from "react";

// Like MUI modals, return focus to whatever opened the dialog. Pass the result
// to the popup's `finalFocus` prop.
export const useReturnFocus = (open) => {
  const returnFocusRef = useRef(null);
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    returnFocusRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
  }, [open]);

  return () => {
    const target = returnFocusRef.current;
    return target?.isConnected ? target : true;
  };
};

// For menus whose items can open a dialog (Remove, Logout…): run the item's
// action after the menu has closed and returned focus to its trigger, so the
// dialog opens last and keeps focus. Pass `onOpenChangeComplete` to the menu
// root and wrap item actions with `runAfterClose`.
export const useRunAfterMenuClose = () => {
  const pending = useRef(null);
  const runAfterClose = useCallback((action) => {
    pending.current = action;
  }, []);
  const onOpenChangeComplete = useCallback((open) => {
    if (open || !pending.current) return;
    const action = pending.current;
    pending.current = null;
    // Next tick: the menu has finished returning focus to its trigger.
    setTimeout(action, 0);
  }, []);
  return { runAfterClose, onOpenChangeComplete };
};
